import { memo } from 'react';
import { Typography as MUITypography, TypographyProps as MUITypographyProps } from '@mui/material';
import classNames from 'classnames';
import { AB3 } from '../../theme';
import { mergeSX } from '../../services/helpers';

interface IBuildSXParams {
  lineClamp?: number;
  backgroundColor?: string;
  disableMinHeight?: boolean;
}

export const TYPOGRAPHY_CAPTION = {
  fontWeight: 600,
  fontSize: 12,
  lineHeight: '18px',
};

export const TYPOGRAPHY_SUBTITLE1 = {
  fontWeight: 700,
  fontSize: 16,
  lineHeight: '22px',
};

export const TYPOGRAPHY_SUBTITLE2 = {
  fontWeight: 600,
  fontSize: 14,
  lineHeight: '19px',
};

export const TYPOGRAPHY_BODY1 = {
  fontWeight: 400,
  fontSize: 16,
  lineHeight: '22px',
};

export const TYPOGRAPHY_BODY2 = {
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '19px',
};

export const TYPOGRAPHY_H5 = {
  fontWeight: 400,
  fontSize: 24,
  lineHeight: '36px',
};

const buildSX = ({ backgroundColor, lineClamp, disableMinHeight }: IBuildSXParams) => ({
  backgroundColor,
  '&.link': {
    color: AB3,
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  '&.lineClamp': {
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lineClamp,
  },
  '&.MuiTypography-h1': {
    fontWeight: 300,
    fontSize: 96,
    lineHeight: '144px',
    minHeight: disableMinHeight ? 'none' : '144px',
  },
  '&.MuiTypography-h2': {
    fontWeight: 300,
    fontSize: 60,
    lineHeight: '90px',
    minHeight: disableMinHeight ? 'none' : '90px',
  },
  '&.MuiTypography-h3': {
    fontWeight: 400,
    fontSize: 48,
    lineHeight: '72px',
    minHeight: disableMinHeight ? 'none' : '72px',
  },
  '&.MuiTypography-h4': {
    fontWeight: 400,
    fontSize: 34,
    lineHeight: '51px',
    minHeight: disableMinHeight ? 'none' : '51px',
  },
  '&.MuiTypography-h5': {
    ...TYPOGRAPHY_H5,
    minHeight: disableMinHeight ? 'none' : '36px',
  },
  '&.MuiTypography-h6': {
    fontWeight: 600,
    fontSize: 20,
    lineHeight: '30px',
    minHeight: disableMinHeight ? 'none' : '30px',
  },
  '&.MuiTypography-subtitle1': {
    ...TYPOGRAPHY_SUBTITLE1,
    minHeight: disableMinHeight ? 'none' : '22px',
  },
  '&.MuiTypography-subtitle2': {
    ...TYPOGRAPHY_SUBTITLE2,
    minHeight: disableMinHeight ? 'none' : '19px',
  },
  '&.MuiTypography-body1': {
    ...TYPOGRAPHY_BODY1,
    minHeight: disableMinHeight ? 'none' : '22px',
  },
  '&.MuiTypography-body2': {
    ...TYPOGRAPHY_BODY2,
    minHeight: disableMinHeight ? 'none' : '19px',
  },
  '&.MuiTypography-body3': {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '22px',
    minHeight: disableMinHeight ? 'none' : '22px',
  },
  '&.MuiTypography-caption': {
    ...TYPOGRAPHY_CAPTION,
    minHeight: disableMinHeight ? 'none' : '18px',
  },
  '&.MuiTypography-overline': {
    fontWeight: 500,
    fontSize: 10,
    lineHeight: '15px',
    minHeight: disableMinHeight ? 'none' : '15px',
    letterSpacing: '0.015em',
    textTransform: 'uppercase',
  },
  '&.MuiTypography-buttons': {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '22px',
    minHeight: disableMinHeight ? 'none' : '22px',
    letterSpacing: '0.015em',
  },
});

// TODO: remove 'any, any'
export interface ITypographyProps extends MUITypographyProps<any, any>, IBuildSXParams {
  asLink?: boolean;
}

const Typography = (props: ITypographyProps) => {
  const {
    children,
    lineClamp,
    sx,
    backgroundColor,
    disableMinHeight = false,
    asLink = false, className, ...restProps
  } = props;

  return (
    <MUITypography
      sx={mergeSX(buildSX({ backgroundColor, disableMinHeight, lineClamp }), sx)}
      className={classNames(className, {
        lineClamp: !!lineClamp,
        link: asLink,
      })}
      {...restProps}
    >
      {children}
    </MUITypography>
  );
};

export default memo(Typography);
